import { Code, Divider, Fieldset, Input, List, SimpleGrid, Stack, Text, TextInput, Title } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type { PropsWithChildren } from "react";
import { notEmptyMessage } from "../../helpers/formHelper";
import { SaveButton } from "../button/SaveButton";
import { EmailEditor } from "./EmailEditor";

type EmailEditorFormProps<T extends EmailFormType> = PropsWithChildren<{
  form: UseFormReturnType<T>;
  saveButtonLabel: string;
  onPreview: () => void;
  previewText?: string;
  required?: boolean;
  disabled?: boolean;
  showPlaceholderGroups?: PlaceholderGroup;
}>;

export type EmailFormType = {
  email_template?: string;
  subject?: string;
};

export enum PlaceholderGroup {
  None = 0,
  Receiver = 1 << 0,
  Apprentice = 1 << 1,
  Group = 1 << 2,
  Document = 1 << 3,
}

export const emailTemplateValidation = (value: string | undefined) => (value === "<p></p>" || !value ? notEmptyMessage : null);

export function EmailEditorForm<T extends EmailFormType>({
  form,
  saveButtonLabel,
  onPreview,
  previewText,
  required = true,
  disabled = false,
  showPlaceholderGroups,
  children,
}: EmailEditorFormProps<T>) {
  const showPlaceholderGroup = (placeholderGroup: PlaceholderGroup) =>
    showPlaceholderGroups && (showPlaceholderGroups & placeholderGroup) === placeholderGroup;

  return (
    <Fieldset legend="E-Mail Vorlage">
      <Stack maw="60rem">
        {children}
        <TextInput
          disabled={disabled}
          withAsterisk={required}
          label="Betreff"
          key={form.key("subject")}
          {...form.getInputProps("subject")}
        />
        <EmailEditor
          onPreview={onPreview}
          previewText={previewText}
          withAsterisk={required}
          label="E-Mail"
          key={form.key("email_template")}
          {...form.getInputProps("email_template")}
          disabled={disabled}
        />
        {showPlaceholderGroups === PlaceholderGroup.None || (
          <Input.Wrapper label="Textbausteine">
            <Text c="dimmed" fz="xs">
              Auch im Betreff verfügbar
            </Text>
            <SimpleGrid cols={4} spacing="xs">
              {showPlaceholderGroup(PlaceholderGroup.Receiver) && (
                <Stack>
                  <Title order={6}>Empfänger</Title>
                  <List icon="-">
                    <List.Item>
                      Ansprache: <Code>$salutation</Code>
                    </List.Item>
                    <List.Item>
                      Name: <Code>$full_name</Code>
                    </List.Item>
                    <List.Item>
                      Vorname: <Code>$first_name</Code>
                    </List.Item>
                    <List.Item>
                      Nachname: <Code>$last_name</Code>
                    </List.Item>
                    <List.Item>
                      Absendedatum: <Code>$date</Code>
                    </List.Item>
                  </List>
                </Stack>
              )}
              {showPlaceholderGroup(PlaceholderGroup.Apprentice) && (
                <Stack>
                  <Title order={6}>Lernende/r</Title>
                  <List icon="-">
                    <List.Item>
                      Name: <Code>$ll_full_name</Code>
                    </List.Item>
                    <List.Item>
                      Vorname: <Code>$ll_first_name</Code>
                    </List.Item>
                    <List.Item>
                      Nachname: <Code>$ll_last_name</Code>
                    </List.Item>
                  </List>
                </Stack>
              )}
              {showPlaceholderGroup(PlaceholderGroup.Group) && (
                <Stack>
                  <Title order={6}>Gruppe</Title>
                  <List icon="-">
                    <List.Item>
                      Name: <Code>$group_name</Code>
                    </List.Item>
                    <List.Item>
                      Coach Name: <Code>$coach_full_name</Code>
                    </List.Item>
                  </List>
                </Stack>
              )}
              {showPlaceholderGroup(PlaceholderGroup.Document) && (
                <Stack>
                  <Title order={6}>Dokument</Title>
                  <List icon="-">
                    <List.Item>
                      Dokumenten Link: <Code>$document_link</Code>
                    </List.Item>
                  </List>
                </Stack>
              )}
            </SimpleGrid>
          </Input.Wrapper>
        )}
        <Divider />
        <SaveButton label={saveButtonLabel} />
      </Stack>
    </Fieldset>
  );
}
